import { ComponentType, ReactElement } from 'react';
import { resolveComponent } from './resolve';
import { SiteProvider } from './shared/lib/site';
import { CartProvider } from './shared/lib/cart';
import { DataLayer } from './shared/lib/dataLayer';
import { Site } from './shared/lib/types';
import ErrorBoundary from './shared/components/ErrorBoundary';

export type TreePayload = {
  component: string;
  props?: Record<string, unknown>;
  layoutProps?: Record<string, unknown>;
  site: Site;
};

/**
 * The element tree a payload produces — the single definition of it.
 *
 * The server renders this and the browser hydrates it, and hydration compares the two
 * for equality. Any divergence between them is a mismatch, which React answers by
 * discarding the server's markup and re-rendering: the page still looks right and the
 * server rendering has quietly bought nothing. Nothing about the HTML shows it.
 *
 * That is a bad thing to leave to two copies staying in step by hand, so there is one
 * copy and both sides import it. app.tsx supplies the browser's parts (reading the
 * payload off the DOM, choosing hydrate over render, stylesheets) and server.tsx the
 * server's (registering icons, renderToString), and neither one composes the tree.
 *
 * Note what is *not* parameterised: the cart. CartProvider takes no initial value on
 * either side, so both start empty and fill after mount. The server's markup is cached
 * and served to every visitor, so it must not contain anyone's cart — and the client's
 * first render has to match it, which is the same requirement from the other end.
 */
export function buildTree({ component, props = {}, layoutProps = {}, site }: TreePayload): ReactElement {
  const Component: ComponentType<any> = resolveComponent(component, site.brand);

  // A component names its layout with a static `layoutName`, the way a Next.js page
  // does. Keeping that here rather than in the page means the page never imports its
  // own chrome, every page gets a layout the same way, and the whole thing is one React
  // root — which is what hydration needs.
  const layoutName = (Component as { layoutName?: string }).layoutName;

  // Resolved by name, so there is no type to check the layout's props against — the
  // contract between a layout and its props builder is enforced on the PHP side.
  const Layout: ComponentType<any> | null = layoutName
    ? resolveComponent(layoutName, site.brand)
    : null;

  const page = <Component {...props} />;

  const content = Layout ? (
    <Layout {...layoutProps}>
      <ErrorBoundary name={component}>{page}</ErrorBoundary>
    </Layout>
  ) : (
    page
  );

  return (
    <ErrorBoundary name={component}>
      <SiteProvider site={site}>
        <CartProvider>
          {/*
            Renders nothing and pushes user_context_loaded once the cart request settles, on
            pages whose server has not already pushed it. It sits here rather than in a layout
            because a page without one needs it too, and inside CartProvider because that is
            what it waits on. Safe for the server renderer: it is all effect, and effects do
            not run there.
          */}
          <DataLayer />
          {content}
        </CartProvider>
      </SiteProvider>
    </ErrorBoundary>
  );
}
